/**
 * ACT-1235: Feature Flags (REAL — backed by listFeatureFlags / setFeatureFlag)
 *
 * GraphQL operations:
 *   Query:    AcListFeatureFlags → listFeatureFlags[] { key displayName description enabled updatedAt updatedBy }
 *   Mutation: AcSetFeatureFlag($key, $enabled) → setFeatureFlag { key enabled updatedAt updatedBy }
 *
 * State persists to the DB on every toggle; reload re-queries and confirms.
 */
import { useQuery, useMutation } from "@apollo/client";
import { RefreshCw } from "lucide-react";

import LoadingSpinner from "../../components/LoadingSpinner";
import { AdminWall } from "../components/AdminWall";
import {
  LIST_FEATURE_FLAGS_QUERY,
  SET_FEATURE_FLAG_MUTATION,
  type ListFeatureFlagsResult,
  type AcFeatureFlag,
  type SetFeatureFlagVars,
} from "../graphql/adminOps";

// ---- Toggle ----
interface ToggleProps {
  enabled: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}

function Toggle({ enabled, onChange, disabled }: ToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      disabled={disabled}
      onClick={() => onChange(!enabled)}
      className={
        "relative inline-flex w-10 h-6 rounded-full border-2 transition-colors duration-200 " +
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
        (enabled ? "bg-primary border-primary" : "bg-muted border-border") +
        " disabled:opacity-40 disabled:cursor-not-allowed"
      }
    >
      <span
        className={
          "absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 " +
          (enabled ? "translate-x-4" : "translate-x-0")
        }
      />
    </button>
  );
}

// ---- Flag row ----
function FlagRow({
  flag,
  onToggle,
  toggling,
}: {
  flag: AcFeatureFlag;
  onToggle: (key: string, enabled: boolean) => void;
  toggling: boolean;
}) {
  const updatedDate = flag.updatedAt
    ? new Date(flag.updatedAt).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <div className="flex items-center gap-4 rounded-xl border border-border bg-card px-4 py-3.5">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-semibold text-sm m-0">{flag.displayName}</p>
          <span
            className={
              "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold " +
              (flag.enabled
                ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-300 dark:border-green-700"
                : "bg-muted text-muted-foreground border-border")
            }
          >
            {flag.enabled ? "ON" : "OFF"}
          </span>
        </div>
        {flag.description && (
          <p className="text-xs text-muted-foreground mt-0.5 m-0">{flag.description}</p>
        )}
        <div className="flex items-center gap-2 mt-1">
          <p className="text-[10px] font-mono text-muted-foreground/70 m-0">{flag.key}</p>
          {updatedDate && (
            <p className="text-[10px] text-muted-foreground/60 m-0">
              · updated {updatedDate}
              {flag.updatedBy ? ` by ${flag.updatedBy}` : ""}
            </p>
          )}
        </div>
      </div>
      <Toggle
        enabled={flag.enabled}
        onChange={(v) => onToggle(flag.key, v)}
        disabled={toggling}
      />
    </div>
  );
}

// ---- Main page ----
export default function FeatureFlagsPage() {
  // cache-first: revisiting this tab reads the warm cache instantly.
  // previousData keeps the list visible during an explicit refetch().
  const { data, previousData, loading, error, refetch } = useQuery<ListFeatureFlagsResult>(
    LIST_FEATURE_FLAGS_QUERY,
    { fetchPolicy: "cache-first" },
  );

  const [setFeatureFlag, { loading: toggling }] = useMutation<
    { setFeatureFlag: AcFeatureFlag },
    SetFeatureFlagVars
  >(SET_FEATURE_FLAG_MUTATION, {
    // Update the cache directly so the toggle feels instant,
    // then the network refetch will confirm the persisted value.
    update(cache, { data: mutData }) {
      if (!mutData) return;
      const updated = mutData.setFeatureFlag;
      const existing = cache.readQuery<ListFeatureFlagsResult>({
        query: LIST_FEATURE_FLAGS_QUERY,
      });
      if (!existing) return;
      cache.writeQuery<ListFeatureFlagsResult>({
        query: LIST_FEATURE_FLAGS_QUERY,
        data: {
          listFeatureFlags: existing.listFeatureFlags.map((f) =>
            f.key === updated.key ? updated : f,
          ),
        },
      });
    },
  });

  const handleToggle = (key: string, enabled: boolean) => {
    void setFeatureFlag({ variables: { key, enabled } });
  };

  // Prefer current data, fall back to previousData so the list stays
  // populated during an explicit refetch() rather than flashing a spinner.
  const flags = (data ?? previousData)?.listFeatureFlags ?? [];

  return (
    <div>
      <AdminWall error={error} screenLabel="Feature Flags">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-xl font-bold">Feature Flags</h1>
          <button
            type="button"
            onClick={() => void refetch()}
            className="p-2 rounded-lg border border-border hover:bg-accent transition-colors"
            aria-label="Refresh"
          >
            <RefreshCw className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Atlas Copilot feature flags. Changes persist immediately to the backend.
        </p>

        {loading && flags.length === 0 ? (
          <LoadingSpinner fullScreen={false} label="Loading feature flags..." />
        ) : error ? (
          <div className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3">
            <p className="text-sm text-destructive font-medium">Failed to load feature flags</p>
            <p className="text-xs text-destructive/80 mt-1">{error.message}</p>
          </div>
        ) : flags.length === 0 ? (
          <p className="text-sm text-muted-foreground">No feature flags found.</p>
        ) : (
          <div className="flex flex-col gap-3">
            {flags.map((flag) => (
              <FlagRow
                key={flag.key}
                flag={flag}
                onToggle={handleToggle}
                toggling={toggling}
              />
            ))}
          </div>
        )}
      </AdminWall>
    </div>
  );
}
